import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Alert, Button } from '@betareader/design-system';
import styled from 'styled-components';
import Icon from '../AppIcon';
import {
  CritiqueGroup,
  useRemoveGroupCover,
  useUploadGroupCover,
} from 'src/hooks/useCritiqueGroups';
import { groupCoverUrl, groupSpineCoverUrl } from 'src/utils/critiqueGroupUtil';
import { MediaQuery } from 'src/constants/size';
import { BookSpine } from './bookSpine';

/**
 * Host-only control to upload the group's cover graphic. The cover shows on the
 * group card and header; when unset, those fall back to the linked book's own
 * cover and then the tinted spine, so this is optional polish, not required.
 *
 * The preview mirrors that same fallback chain (`groupSpineCoverUrl`) so the
 * host sees exactly what a card will render — including "you have no cover yet,
 * here's the book cover we'll use instead".
 */

const Panel = styled.div`
  background: var(--br-surface);
  border: 1px solid var(--br-border);
  border-radius: 14px;
  overflow: hidden;
  max-width: 620px;
  margin-bottom: 18px;
`;

const Head = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid var(--br-border);
  font-weight: 800;
  font-size: 17px;
`;

const Body = styled.div`
  padding: 24px;
  display: flex;
  gap: 18px;
  align-items: center;
  flex-wrap: wrap;
`;

const Help = styled.div`
  font-size: 12.5px;
  color: var(--br-muted);
  margin-bottom: 12px;
`;

// The Change/Remove pair. On desktop they sit side by side; on a phone they
// STACK full-width with matching left edges and an even gap — replacing the old
// `marginLeft` + natural-width wrap, which dropped Remove into a ragged,
// indented second row. `gap` owns the spacing.
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .br-button {
    margin: 0;
  }

  ${MediaQuery.phone} {
    flex-direction: column;
    align-items: stretch;
  }
`;

/**
 * What the SERVER can actually decode (sharp re-encodes everything to JPEG on
 * upload) — keep in sync with `allowedMimeTypes` in
 * api/src/routes/critique-groups/uploadCover.js. This list must never be
 * narrower than the server's: anything we reject here is rejected silently, with
 * no request and so no server-side trace of why the host couldn't upload.
 */
const ALLOWED = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'image/avif',
  'image/tiff',
];
/**
 * Matches multer's `fileSize` limit in api/app.ts. It is deliberately the SAME
 * number and not a friendlier smaller one: a cover under the server's limit must
 * never be refused here, and the server downsizes to 1200×1600 anyway, so a big
 * source file is not a problem — only a >10 MB one is, and that we can explain
 * before spending the host's upload bandwidth on it.
 */
const MAX_BYTES = 10 * 1024 * 1024;

const GroupCoverPanel: React.FC<{ group: CritiqueGroup }> = ({ group }) => {
  const { t } = useTranslation();
  const upload = useUploadGroupCover(group._id);
  const remove = useRemoveGroupCover(group._id);
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  // The just-uploaded file's local bytes, shown immediately so the preview
  // repaints in place without waiting on the (possibly not-yet-public) GCS url.
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  // Free the object URL when it's replaced or the panel unmounts.
  useEffect(() => {
    if (!localPreview) return undefined;
    return () => URL.revokeObjectURL(localPreview);
  }, [localPreview]);

  const hasOwnCover = !!groupCoverUrl(group) || !!localPreview;
  const previewUrl = localPreview || groupSpineCoverUrl(group);
  const busy = upload.isPending || remove.isPending;

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    // Reset immediately so picking the same file twice still fires onChange.
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;

    // Deliberately mirrors the server exactly, including for a file the browser
    // couldn't type: those upload as application/octet-stream, which the server
    // refuses too, so passing them on would cost the host the upload and return
    // the same answer.
    if (!ALLOWED.includes(file.type)) {
      setError(
        t(
          'CritiqueCoverBadType',
          'Please choose a PNG, JPEG, WebP, GIF, AVIF or TIFF image.'
        )
      );
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(t('CritiqueCoverTooLarge', 'The image must be under 10 MB.'));
      return;
    }

    upload.mutate(
      { file },
      {
        onSuccess: () => {
          // Show the host their own image at once (the cache also now holds the
          // real url for the header/cards).
          setLocalPreview(URL.createObjectURL(file));
          toast.success(t('CritiqueCoverSaved', 'Group cover updated'));
        },
        onError: err =>
          setError(
            err?.message ||
              t('CritiqueCoverFailed', 'Could not upload the cover.')
          ),
      }
    );
  };

  const onRemove = () => {
    setError(null);
    remove.mutate(undefined, {
      onSuccess: () => {
        setLocalPreview(null);
        toast.success(t('CritiqueCoverRemoved', 'Group cover removed'));
      },
      onError: err =>
        setError(
          err?.message ||
            t('CritiqueCoverRemoveFailed', 'Could not remove the cover.')
        ),
    });
  };

  return (
    <Panel data-testid="group-cover-panel">
      <Head>{t('CritiqueGroupCover', 'Group cover')}</Head>
      <Body>
        <BookSpine
          title={group.name}
          coverUrl={previewUrl}
          seed={group._id}
          width={72}
          height={102}
        />
        <div style={{ flex: 1, minWidth: 220 }}>
          <Help>
            {t(
              'CritiqueGroupCoverHelp',
              'Upload a cover for this group. If you don’t, we’ll use your linked book’s cover.'
            )}
          </Help>
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED.join(',')}
            onChange={onPick}
            style={{ display: 'none' }}
            data-testid="group-cover-input"
          />
          <Actions>
            <Button
              variant="secondary"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              aria-busy={upload.isPending}
              style={{ borderRadius: 9, fontWeight: 700 }}
              data-testid="group-cover-upload"
            >
              <Icon name="image" aria-hidden="true" />
              {hasOwnCover
                ? t('CritiqueChangeCover', 'Change cover')
                : t('CritiqueUploadCover', 'Upload cover')}
            </Button>
            {hasOwnCover && (
              <Button
                variant="secondary"
                onClick={onRemove}
                disabled={busy}
                aria-busy={remove.isPending}
                style={{ borderRadius: 9, fontWeight: 700, color: 'var(--br-error-text)' }}
                data-testid="group-cover-remove"
              >
                <Icon name="trash alternate" aria-hidden="true" />
                {t('CritiqueRemoveCover', 'Remove cover')}
              </Button>
            )}
          </Actions>
          {error && (
            <Alert variant="error" style={{ marginTop: 12 }}>
              {error}
            </Alert>
          )}
        </div>
      </Body>
    </Panel>
  );
};

export default GroupCoverPanel;
